import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Save, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useToast } from '../../hooks/use-toast';
import { settingsAPI, solutionsAPI, capabilitiesAPI } from '../../services/api';

const ContentSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  
  // Hero Stats
  const [stats, setStats] = useState({
    years_experience: '15+',
    projects_completed: '100+',
    happy_clients: '50+'
  });

  // Hero Image
  const [heroImage, setHeroImage] = useState({
    url: '',
    alt: 'Digital Transformation'
  });

  // About Section
  const [about, setAbout] = useState({
    title_id: 'Siapa Kami?',
    title_en: 'Who We Are?',
    description_id: '',
    description_en: ''
  });

  // Solutions
  const [solutions, setSolutions] = useState([]);

  // Capabilities
  const [capabilities, setCapabilities] = useState([]);
  const [newCapability, setNewCapability] = useState('');

  // Load data from API on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [settingsData, solutionsData, capabilitiesData] = await Promise.all([
        settingsAPI.getAll(),
        solutionsAPI.getAll(),
        capabilitiesAPI.getAll()
      ]);

      // Set stats
      setStats({
        years_experience: settingsData.stats_years_experience || '15+',
        projects_completed: settingsData.stats_projects_completed || '100+',
        happy_clients: settingsData.stats_happy_clients || '50+'
      });

      // Set hero image
      setHeroImage({
        url: settingsData.hero_image_url || '',
        alt: settingsData.hero_image_alt || 'Digital Transformation'
      });

      // Set about
      setAbout({
        title_id: settingsData.about_title_id || 'Siapa Kami?',
        title_en: settingsData.about_title_en || 'Who We Are?',
        description_id: settingsData.about_description_id || '',
        description_en: settingsData.about_description_en || ''
      });

      // Set solutions and capabilities
      setSolutions(solutionsData);
      setCapabilities(capabilitiesData);
      
    } catch (error) {
      console.error('Error fetching content data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load content data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Save Stats
  const handleSaveStats = async () => {
    try {
      await settingsAPI.update({
        stats_years_experience: stats.years_experience,
        stats_projects_completed: stats.projects_completed,
        stats_happy_clients: stats.happy_clients
      });
      toast({ title: 'Statistics updated successfully!' });
    } catch (error) {
      console.error('Error saving stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to save statistics',
        variant: 'destructive'
      });
    }
  };

  // Save Hero Image
  const handleSaveHeroImage = async () => {
    try {
      await settingsAPI.update({
        hero_image_url: heroImage.url,
        hero_image_alt: heroImage.alt
      });
      toast({ title: 'Hero image updated successfully!' });
    } catch (error) {
      console.error('Error saving hero image:', error);
      toast({
        title: 'Error',
        description: 'Failed to save hero image',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveHeroImage = async () => {
    try {
      await settingsAPI.update({
        hero_image_url: ''
      });
      setHeroImage({ ...heroImage, url: '' });
      toast({ title: 'Hero image removed' });
    } catch (error) {
      console.error('Error removing hero image:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove hero image',
        variant: 'destructive'
      });
    }
  };

  // Save About
  const handleSaveAbout = async () => {
    try {
      await settingsAPI.update({
        about_title_id: about.title_id,
        about_title_en: about.title_en,
        about_description_id: about.description_id,
        about_description_en: about.description_en
      });
      toast({ title: 'About section updated successfully!' });
    } catch (error) {
      console.error('Error saving about:', error);
      toast({
        title: 'Error',
        description: 'Failed to save about section',
        variant: 'destructive'
      });
    }
  };

  // Solutions CRUD
  const handleUpdateSolution = async (id, field, value) => {
    setSolutions(solutions.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const handleSaveSolutions = async () => {
    try {
      // Update all solutions
      for (const solution of solutions) {
        if (solution.id) {
          await solutionsAPI.update(solution.id, solution);
        } else {
          await solutionsAPI.create(solution);
        }
      }
      toast({ title: 'Solutions updated successfully!' });
      fetchAllData(); // Refresh data
    } catch (error) {
      console.error('Error saving solutions:', error);
      toast({
        title: 'Error',
        description: 'Failed to save solutions',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteSolution = async (id) => {
    if (solutions.length <= 1) {
      toast({ 
        title: 'Cannot delete! At least one solution is required.', 
        variant: 'destructive' 
      });
      return;
    }
    
    try {
      if (id) {
        await solutionsAPI.delete(id);
      }
      setSolutions(solutions.filter(s => s.id !== id));
      toast({ title: 'Solution deleted successfully' });
    } catch (error) {
      console.error('Error deleting solution:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete solution',
        variant: 'destructive'
      });
    }
  };

  const handleAddSolution = () => {
    const newSolution = {
      id: null, // Will be assigned by backend
      title_id: 'Solusi Baru',
      title_en: 'New Solution',
      description_id: 'Deskripsi solusi...',
      description_en: 'Solution description...',
      display_order: solutions.length
    };
    setSolutions([...solutions, newSolution]);
  };

  // Capabilities CRUD
  const handleAddCapability = async () => {
    if (newCapability.trim()) {
      try {
        const newCap = await capabilitiesAPI.create({
          title: newCapability.trim(),
          display_order: capabilities.length
        });
        setCapabilities([...capabilities, newCap]);
        setNewCapability('');
        toast({ title: 'Capability added successfully' });
      } catch (error) {
        console.error('Error adding capability:', error);
        toast({
          title: 'Error',
          description: 'Failed to add capability',
          variant: 'destructive'
        });
      }
    }
  };

  const handleUpdateCapability = async (id, value) => {
    try {
      await capabilitiesAPI.update(id, { title: value });
      setCapabilities(capabilities.map(c => 
        c.id === id ? { ...c, title: value } : c
      ));
    } catch (error) {
      console.error('Error updating capability:', error);
    }
  };

  const handleDeleteCapability = async (id) => {
    try {
      await capabilitiesAPI.delete(id);
      setCapabilities(capabilities.filter(c => c.id !== id));
      toast({ title: 'Capability deleted successfully' });
    } catch (error) {
      console.error('Error deleting capability:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete capability',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center py-12">Loading...</div>;
  }

  return (
    <Tabs defaultValue="hero" className="w-full">
      <TabsList className="grid w-full grid-cols-5">
        <TabsTrigger value="hero">Hero Section</TabsTrigger>
        <TabsTrigger value="stats">Stats</TabsTrigger>
        <TabsTrigger value="about">About</TabsTrigger>
        <TabsTrigger value="solutions">Solutions</TabsTrigger>
        <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
      </TabsList>

      {/* Hero Section Tab */}
      <TabsContent value="hero" className="space-y-6 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Hero Section Image</CardTitle>
            <CardDescription>Upload and manage the main hero section image</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {heroImage.url ? (
              <div className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-4 bg-gray-50">
                  <img 
                    src={heroImage.url} 
                    alt={heroImage.alt}
                    className="max-w-full max-h-96 object-contain mx-auto rounded-lg"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveHeroImage}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove Image
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center text-sm text-gray-500">Enter image URL below</div>
                <Input 
                  placeholder="Enter image URL (e.g., https://unsplash.com/...)" 
                  value={heroImage.url}
                  onChange={(e) => setHeroImage({ ...heroImage, url: e.target.value })}
                />
              </div>
            )}

            <div>
              <Label>Image Alt Text</Label>
              <Input 
                placeholder="e.g., Digital Transformation" 
                value={heroImage.alt}
                onChange={(e) => setHeroImage({ ...heroImage, alt: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">For SEO and accessibility</p>
            </div>

            <Button onClick={handleSaveHeroImage} className="bg-gradient-to-r from-cyan-500 to-teal-600 w-full">
              <Save className="w-4 h-4 mr-2" />
              Save Hero Image
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Hero Stats Tab */}
      <TabsContent value="stats" className="space-y-4 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Hero Section Statistics</CardTitle>
            <CardDescription>Edit the statistics displayed in the hero section</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Years Experience</Label>
                <Input 
                  value={stats.years_experience}
                  onChange={(e) => setStats({ ...stats, years_experience: e.target.value })}
                  placeholder="15+"
                />
                <p className="text-xs text-gray-500 mt-1">e.g., 15+ atau 20+</p>
              </div>
              <div>
                <Label>Projects Completed</Label>
                <Input 
                  value={stats.projects_completed}
                  onChange={(e) => setStats({ ...stats, projects_completed: e.target.value })}
                  placeholder="100+"
                />
                <p className="text-xs text-gray-500 mt-1">e.g., 100+ atau 200+</p>
              </div>
              <div>
                <Label>Happy Clients</Label>
                <Input 
                  value={stats.happy_clients}
                  onChange={(e) => setStats({ ...stats, happy_clients: e.target.value })}
                  placeholder="50+"
                />
                <p className="text-xs text-gray-500 mt-1">e.g., 50+ atau 100+</p>
              </div>
            </div>
            <Button onClick={handleSaveStats} className="bg-gradient-to-r from-cyan-500 to-teal-600">
              <Save className="w-4 h-4 mr-2" />
              Save Statistics
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      {/* About Tab */}
      <TabsContent value="about" className="space-y-4 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>About Section</CardTitle>
            <CardDescription>Edit "Who We Are" section content</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title (Indonesian)</Label>
                <Input 
                  value={about.title_id}
                  onChange={(e) => setAbout({ ...about, title_id: e.target.value })}
                />
              </div>
              <div>
                <Label>Title (English)</Label>
                <Input 
                  value={about.title_en}
                  onChange={(e) => setAbout({ ...about, title_en: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Description (Indonesian)</Label>
              <Textarea 
                value={about.description_id}
                onChange={(e) => setAbout({ ...about, description_id: e.target.value })}
                rows={4}
              />
            </div>
            <div>
              <Label>Description (English)</Label>
              <Textarea 
                value={about.description_en}
                onChange={(e) => setAbout({ ...about, description_en: e.target.value })}
                rows={4}
              />
            </div>
            <Button onClick={handleSaveAbout} className="bg-gradient-to-r from-cyan-500 to-teal-600">
              <Save className="w-4 h-4 mr-2" />
              Save About Section
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Solutions Tab */}
      <TabsContent value="solutions" className="space-y-4 mt-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Solution Portfolio</h3>
            <p className="text-sm text-gray-600">Manage your solution offerings</p>
          </div>
          <Button onClick={handleAddSolution} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add Solution
          </Button>
        </div>

        <div className="space-y-4">
          {solutions.map((solution, index) => (
            <Card key={solution.id || index}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <GripVertical className="w-5 h-5 text-gray-400" />
                    <span className="font-semibold text-gray-700">Solution {index + 1}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteSolution(solution.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Title (Indonesian)</Label>
                      <Input 
                        value={solution.title_id}
                        onChange={(e) => handleUpdateSolution(solution.id || index, 'title_id', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Title (English)</Label>
                      <Input 
                        value={solution.title_en}
                        onChange={(e) => handleUpdateSolution(solution.id || index, 'title_en', e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Description (Indonesian)</Label>
                    <Textarea 
                      value={solution.description_id}
                      onChange={(e) => handleUpdateSolution(solution.id || index, 'description_id', e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Description (English)</Label>
                    <Textarea 
                      value={solution.description_en}
                      onChange={(e) => handleUpdateSolution(solution.id || index, 'description_en', e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Button onClick={handleSaveSolutions} className="bg-gradient-to-r from-cyan-500 to-teal-600 w-full">
          <Save className="w-4 h-4 mr-2" />
          Save All Solutions
        </Button>
      </TabsContent>

      {/* Capabilities Tab */}
      <TabsContent value="capabilities" className="space-y-4 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Our Capabilities</CardTitle>
            <CardDescription>Manage the list of company capabilities</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input 
                value={newCapability}
                onChange={(e) => setNewCapability(e.target.value)}
                placeholder="Add new capability..."
                onKeyPress={(e) => e.key === 'Enter' && handleAddCapability()}
              />
              <Button onClick={handleAddCapability}>
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>

            <div className="space-y-2">
              {capabilities.map((capability) => (
                <div key={capability.id} className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50">
                  <GripVertical className="w-5 h-5 text-gray-400" />
                  <Input 
                    value={capability.title}
                    onChange={(e) => handleUpdateCapability(capability.id, e.target.value)}
                    className="flex-1 bg-white"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteCapability(capability.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default ContentSettings;
